import { useCallback, useEffect, useState } from "react";
import { errorMessage, request } from "../lib/api";
import { track } from "../lib/events";
import type {
  PDDayDetail,
  PDDayProgressResponse,
  PDDayStats,
  RosterEntry,
  RosterResponse,
} from "../types";

const POLL_MS = 20000;

export interface LiveProgress {
  detail: PDDayDetail;
  stats: PDDayStats;
  progress: PDDayProgressResponse;
  roster: RosterEntry[];
  presentCount: number;
  absentCount: number;
  syncedAt: number;
}

export interface LiveProgressState {
  data: LiveProgress | null;
  loading: boolean;
  error: string;
  reload: () => Promise<void>;
}

async function fetchLiveProgress(sessionID: string): Promise<LiveProgress> {
  const [detail, stats, roster, progress] = await Promise.all([
    request<PDDayDetail>(`/api/facilitator/pd-days/${sessionID}`),
    request<PDDayStats>(`/api/facilitator/pd-days/${sessionID}/stats`),
    request<RosterResponse>(`/api/facilitator/pd-days/${sessionID}/roster`),
    request<PDDayProgressResponse>(`/api/facilitator/pd-days/${sessionID}/progress`),
  ]);
  return {
    detail,
    stats,
    progress,
    roster: roster.roster,
    presentCount: roster.roster.filter((entry) => entry.attendance === "present").length,
    absentCount: roster.roster.filter((entry) => entry.attendance === "absent").length,
    syncedAt: Date.now(),
  };
}

export function useLiveProgress(sessionID: string): LiveProgressState {
  const [state, setState] = useState<Omit<LiveProgressState, "reload">>({
    data: null,
    loading: true,
    error: "",
  });

  const reload = useCallback(
    () =>
      fetchLiveProgress(sessionID)
        .then((data) => setState({ data, loading: false, error: "" }))
        .catch((cause) =>
          setState((current) => ({ ...current, loading: false, error: errorMessage(cause) }))
        ),
    [sessionID]
  );

  useEffect(() => {
    track("page_opened", { payload: { page: "pd_day_progress", session_id: sessionID } });
    fetchLiveProgress(sessionID)
      .then((data) => setState({ data, loading: false, error: "" }))
      .catch((cause) =>
        setState((current) => ({ ...current, loading: false, error: errorMessage(cause) }))
      );
  }, [sessionID]);

  // Only a running session produces new numbers; a scheduled or ended one
  // would just re-fetch the same rows every 20 seconds.
  useEffect(() => {
    if (state.data?.detail.status !== "in_progress") return undefined;
    const timer = setInterval(() => void reload(), POLL_MS);
    return () => clearInterval(timer);
  }, [state.data?.detail.status, reload]);

  return { ...state, reload };
}

// useSecondsSince re-renders on a slow tick so the "synced Ns ago" label
// stays honest between polls without spinning at 1Hz. Disabled once the
// session ends: nothing is being fetched any more, so a counter that keeps
// climbing would claim otherwise.
export function useSecondsSince(timestamp: number, enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(timer);
  }, [enabled]);

  return Math.max(0, Math.round((Math.max(now, timestamp) - timestamp) / 1000));
}
